<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-aqua" sprite="group-3" />
          Conference Rooms
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-aqua" sprite="group-3" />
            Conference Rooms
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
          <el-button
            class="ml-10 hidden-xs-only"
            @click="doExport"
          >
            Export
          </el-button>
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :createVisible="createVisible"
      :clearFormFields="clearFormFields"
      :createProcessing="createProcessing"
      :numInUse="numInUse"
      @checkNumber="checkNumber"
      @createOperation="createOperation"
      @cancelOperation="cancelOperation"
    />

    <edit-form
      :editVisible="editVisible"
      :clearFormFields="clearFormFields"
      :editProcessing="editProcessing"
      :selectedConfBridge="selectedConfBridge"
      :numInUse="numInUse"
      @checkNumber="checkNumber"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import feathersClient from "../../../feathers/feathers-client";
import { debounce } from "lodash";
import { globalEventBus } from "../../../globalEventBus";

import CreateForm from "./components/createForm";
import EditForm from "./components/editForm";
import {ExportToCsv} from "export-to-csv";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "ConferenceBridgesTable",
  data() {
    return {
      total: 0,
      eventChannel: null,
      tableData: [],
      allRooms: [],
      selectedConfBridge: null,
      search: "",
      number: "",
      visibleActionsId: "",
      numInUse: null,
      editVisible: false,
      createVisible: false,
      clearFormFields: false,
      createProcessing: false,
      editProcessing: false,
      deleteProcessing: false,
      tableLoading: false,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-aqua" sprite="group-3" />
                </div>
              ];
            }
          }
        },
        {
          field: "name",
          title: "Name",
          minWidth: 150,
          sortable: true
        },
        {
          field: "number",
          title: "Number",
          minWidth: 100,
          sortable: true,
          slots: {
            default: ({row}) => {
              return [
                <span>{this.numberFormatter(row.number)}</span>
              ]
            }
          }
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  components: { CreateForm, EditForm },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    selectedTenant() {
      this.findConferenceBridges();
      feathersClient
        .service("channel-memberships")
        .remove(null, { query: { channelName: this.eventChannel } });
      this.joinSocketChannel();
    },
    user() {
      this.findConferenceBridges();
      if (!this.eventChannel) {
        this.joinSocketChannel();
      }
    },

    number: debounce(function() {
      const checkNumberService = feathersClient.service("check-number");
      checkNumberService
        .find({
          query: { tenantId: this.selectedTenant.id, number: this.number }
        })
        .then(res => {
          this.numInUse = res;
        });
    }, 500),

    search: debounce(function() {
      this.searchRecords();
    }, 500)
  },

  methods: {
    ...mapActions("conference-bridges", {
      createConferenceBridges: "create",
      patchConferenceBridges: "patch",
      deleteConferenceBridges: "remove"
    }),

    doExport(){
      const data = [...this.tableData];
      if(data.length){

        const writeData = data.map(item => ({
          name: item.name,
          number: item.number ? item.number : ''
        }));

        const options = {
          filename: 'conference_rooms',
          showLabels: true,
          headers: ['Name', 'Number']
        }

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    searchRecords() {
      if (this.search.length) {
        this.tableData = this.allRooms.filter(
          data =>
            !this.search ||
            data.number.toLowerCase().includes(this.search.toLowerCase()) ||
            data.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else {
        this.tableData = [...this.allRooms];
      }
      this.total = this.tableData.length;
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    openEditModal(id) {
      this.clearFormFields = false;
      feathersClient
        .service("conference-bridges")
        .get(id)
        .then(res => {
          this.selectedConfBridge = res;
          this.editVisible = true;
        });
    },

    findConferenceBridges() {
      this.tableLoading = true;
      feathersClient
        .service("conference-bridges")
        .find({
          query: {
            tenantId: this.selectedTenant.id
          }
        })
        .then(res => {
          this.tableData = [...res];
          this.allRooms = [...res];
          this.total = res.length;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    // ⭐️ CRUD OPERATIONS ⭐️ //
    createOperation(createBody) {
      this.$confirm("Create conference room?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.createProcessing = true;

          this.createConferenceBridges(createBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success"
              });
              this.clearFormFields = true;
              this.createProcessing = false;
              this.cancelOperation("create");
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
              this.createProcessing = false;
              this.clearFormFields = false;
            });
        })
        .catch(() => true);
    },

    editOperation(editBody) {
      this.$confirm("Save conference bridge?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.editProcessing = true;

          this.patchConferenceBridges([this.selectedConfBridge.id, editBody])
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Edited successfully!",
                type: "success"
              });
              this.editProcessing = false;
              this.clearFormFields = true;
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.clearFormFields = false;
              this.editProcessing = false;
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
            });
        })
        .catch(() => true);
    },

    deleteOperation(id) {
      this.$confirm("Delete conference bridge?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.deleteProcessing = true;

          this.deleteConferenceBridges(id)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success"
              });
              this.deleteProcessing = false;
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
              this.deleteProcessing = false;
            });
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
        this.clearFormFields = true;
      } else if (type === "edit") {
        this.editVisible = false;
        this.clearFormFields = true;
      }
      this.numInUse = null;
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    checkNumber(num) {
      this.number = num;
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    generateRandomKey() {
      const generateRandomString = () =>
        Math.random()
          .toString(36)
          .slice(2);
      return `${generateRandomString()}-${generateRandomString()}$${generateRandomString()}`;
    },

    joinSocketChannel() {
      const channelService = feathersClient.service("channel-memberships");
      const data = {
        roomType: "admins",
        tenantId: this.selectedTenant.id
      };
      channelService.create(data).then(res => {
        this.eventChannel = `${data.roomType}/${data.tenantId}`;
      });
    },

    removeListeners() {
      feathersClient.service("conference-bridges").removeListener("patched");
      feathersClient.service("conference-bridges").removeListener("created");
      feathersClient.service("conference-bridges").removeListener("removed");
      globalEventBus.$off("appReconnected");
    }
  },
  created() {
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    //record was edited
    //always refresh did destination options
    feathersClient.service("conference-bridges").on("patched", data => {
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allRooms.findIndex(
        item => item.id === data.id
      );
      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allRooms.splice(updateAllIndex, 1, data);
      }
    });

    //record was created
    feathersClient.service("conference-bridges").on("created", data => {
      this.tableData.push(data);
      this.allRooms.push(data);
    });

    //record was deleted
    feathersClient.service("conference-bridges").on("removed", data => {
      const indexToRemove = this.tableData.findIndex(
        item => item.id === data.id
      );
      const indexAllToRemove = this.allRooms.findIndex(
        item => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allRooms.splice(indexAllToRemove, 1);
      }
    });
  },
  mounted() {
    if (this.user) {
      this.findConferenceBridges();
      this.joinSocketChannel();
    }
  },
  beforeDestroy() {
    this.removeListeners();
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.eventChannel } });
  }
};
</script>
